/**
 * 회원 목록 — 마스터-디테일
 *
 *  매장 관리(/business) 와 동일한 패턴.
 *  좌측: 가입 경로(SNS) 탭 + 검색 + 회원 리스트 (무한 스크롤)
 *  우측: 선택된 회원 상세 (탭 구성)
 *
 *  정렬은 서버(dtRegist desc) 순서를 그대로 사용 — 최근 가입한 회원이 위로 온다.
 *  회원 유형으로 탭을 나누지 않는 이유는 memberCodes.ts 주석 참고.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Search, Users } from 'lucide-react';
import {
  membersApi,
  type CustomerListResponseDto,
  type GetMembersResponse,
  type MemberSnsTypeCode,
} from '@/services/api';
import { StatusBadge } from '@/components/common';
import { MemberDetailPanel } from './MemberDetailPanel';
import { SNS_LABEL, SNS_TABS } from './memberCodes';

type SnsTab = 'ALL' | MemberSnsTypeCode | 'TEST';

/** 무한 스크롤 한 번에 불러올 건수 */
const FETCH_SIZE = 30;

/** 바닥에서 이 거리 안으로 들어오면 다음 페이지를 당겨온다 */
const SCROLL_THRESHOLD_PX = 200;

const formatDate = (s?: string) => (s ? new Date(s).toLocaleDateString('ko-KR') : '-');

export function MemberListPage() {
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState<string | undefined>();
  const [tab, setTab] = useState<SnsTab>('ALL');
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);

  const listRef = useRef<HTMLDivElement>(null);

  // 검색어 디바운스 (300ms)
  useEffect(() => {
    const t = setTimeout(() => setAppliedKeyword(keyword.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  const isTestTab = tab === 'TEST';
  const snsParam = tab === 'ALL' || isTestTab ? undefined : tab;
  const testParam = isTestTab ? 'Y' : undefined;

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['members', 'infinite', { keyword: appliedKeyword, snsType: snsParam, testYn: testParam }],
    queryFn: ({ pageParam }) =>
      membersApi.getMembers({
        page: pageParam,
        size: FETCH_SIZE,
        keyword: appliedKeyword,
        snsType: snsParam,
        testYn: testParam,
      }),
    initialPageParam: 0,
    // last/number 값에 의존하지 않고 누적 건수로 판단
    getNextPageParam: (lastPage: GetMembersResponse, allPages: GetMembersResponse[]) => {
      const loaded = allPages.reduce((n, p) => n + (p.content?.length ?? 0), 0);
      const total = lastPage.totalElements ?? 0;
      if (loaded === 0 || loaded >= total) return undefined;
      return allPages.length;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const members = useMemo<CustomerListResponseDto[]>(
    () => (data?.pages ?? []).flatMap(p => (p.content ?? []) as CustomerListResponseDto[]),
    [data],
  );
  const totalElements = data?.pages?.[0]?.totalElements ?? 0;

  // 탭 배지용 전체 건수 — 현재 검색어 기준 (size 1 로 count 만 사용)
  const countAll = useTabCount(appliedKeyword, undefined);
  const countKakao = useTabCount(appliedKeyword, 'SNS_TYPE_KAKAO');
  const countNaver = useTabCount(appliedKeyword, 'SNS_TYPE_NAVER');
  const countGoogle = useTabCount(appliedKeyword, 'SNS_TYPE_GOOGLE');
  const countApple = useTabCount(appliedKeyword, 'SNS_TYPE_APPLE');
  const countTest = useTabCount(appliedKeyword, undefined, 'Y');
  const tabCounts: Record<SnsTab, number | undefined> = {
    ALL: countAll,
    SNS_TYPE_KAKAO: countKakao,
    SNS_TYPE_NAVER: countNaver,
    SNS_TYPE_GOOGLE: countGoogle,
    SNS_TYPE_APPLE: countApple,
    TEST: countTest,
  };

  // 탭/검색이 바뀌면 목록 스크롤을 맨 위로
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [appliedKeyword, tab]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD_PX) {
      fetchNextPage();
    }
  };

  return (
    // 페이지 전체를 한 화면에 — 헤더(64) + main 패딩 고려해 calc
    <div className="flex h-[calc(100vh-120px)] flex-col">
      <header className="mb-3 shrink-0">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
          <Users className="text-gray-500" size={22} /> 회원 목록
        </h1>
        <p className="mt-0.5 text-xs text-gray-500">
          최근 가입한 회원 순으로 표시됩니다. 좌측에서 회원을 선택하면 우측에 상세 정보가 탭으로
          표시됩니다.
        </p>
      </header>

      {/* 좌측 리스트 + 우측 상세 — Master-Detail (flex-1, 내부 스크롤) */}
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        {/* ── 좌측: 회원 리스트 (5/12) ── */}
        <div className="col-span-12 flex min-h-0 flex-col lg:col-span-5">
          {/* 검색 */}
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder="회원명 / 닉네임 / 이메일"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* 가입 경로 탭 */}
          <div className="mb-2 flex shrink-0 gap-1 border-b border-gray-200">
            {SNS_TABS.map(t => {
              const active = tab === t.key;
              const count = tabCounts[t.key];
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`-mb-px shrink-0 border-b-2 px-2.5 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {t.label}
                  {count != null && (
                    <span
                      className={`ml-1 text-xs tabular-nums ${
                        active ? 'text-primary-500' : 'text-gray-400'
                      }`}
                    >
                      {count.toLocaleString()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
            <span>
              {members.length.toLocaleString()} / {totalElements.toLocaleString()}명 표시
            </span>
            {isFetching && !isFetchingNextPage && !isLoading && (
              <span className="text-gray-400">불러오는 중…</span>
            )}
          </div>

          {/* 리스트 — flex-1 로 남은 공간 차지, 내부 스크롤 + 무한 로딩 */}
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="h-full divide-y divide-gray-100 overflow-y-auto"
            >
              {isLoading && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">로딩 중…</div>
              )}
              {!isLoading && error && (
                <div className="px-4 py-8 text-center text-sm text-red-500">
                  회원 목록을 불러오지 못했습니다.
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="ml-2 underline hover:text-red-700"
                  >
                    다시 시도
                  </button>
                </div>
              )}
              {!isLoading && !error && members.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  {appliedKeyword ? '검색 결과가 없습니다.' : '등록된 회원이 없습니다.'}
                </div>
              )}
              {!isLoading &&
                !error &&
                members.map(m => {
                  const selected = selectedSeq === m.userSeq;
                  return (
                    <div
                      key={m.userSeq}
                      onClick={() => setSelectedSeq(m.userSeq)}
                      // 컨테이너의 divide-gray-100 규칙(명시도 0,3,0)이 border-color 단축 속성으로
                      // 좌측 테두리 색까지 덮어써서, ! 프리픽스로 선택 표시를 강제한다.
                      className={`cursor-pointer px-3 py-2.5 transition-colors ${
                        selected
                          ? 'border-l-4 !border-l-primary-500 bg-primary-50 pl-2'
                          : 'border-l-4 !border-l-transparent hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-gray-900">
                              {m.userName || `회원 #${m.userSeq}`}
                            </span>
                            {m.nickname && (
                              <span className="truncate text-xs text-gray-500">({m.nickname})</span>
                            )}
                            {m.marketingConsent === 'Y' && (
                              <StatusBadge tone="success">마케팅</StatusBadge>
                            )}
                            {m.testYn === 'Y' && (
                              <StatusBadge tone="danger">TEST</StatusBadge>
                            )}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-gray-500">
                            {m.email || '이메일 없음'}
                            {m.phoneNumber ? ` · ${m.phoneNumber}` : ''}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-gray-400">
                            {SNS_LABEL[m.snsType] ?? m.snsType} 가입 · SEQ {m.userSeq}
                            {m.usedRegion ? ` · ${m.usedRegion}` : ''}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-[11px] text-gray-600">
                            반려동물 {m.petCount ?? 0}마리
                          </div>
                          <div className="mt-0.5 text-[10px] text-gray-500">
                            이용사업자 {m.usedBusinessCount ?? 0}개
                          </div>
                          <div className="mt-0.5 text-[10px] text-gray-400">
                            {formatDate(m.regDt)} 가입
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

              {/* 무한 스크롤 하단 — 자동 로딩 + 수동 더 보기 */}
              {!isLoading && !error && members.length > 0 && (
                <div className="px-4 py-3 text-center">
                  {isFetchingNextPage ? (
                    <span className="text-xs text-gray-400">더 불러오는 중…</span>
                  ) : hasNextPage ? (
                    <button
                      type="button"
                      onClick={() => fetchNextPage()}
                      className="rounded-md border border-gray-200 px-3 py-1 text-xs text-gray-600 hover:border-primary-500 hover:text-primary-600"
                    >
                      더 보기
                    </button>
                  ) : (
                    <span className="text-xs text-gray-300">마지막 회원입니다</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── 우측: 상세 (7/12) ── */}
        <div className="col-span-12 min-h-0 lg:col-span-7">
          {selectedSeq === null ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
              <div className="text-center">
                <Users className="mx-auto mb-2 text-gray-300" size={40} />
                <div className="text-sm text-gray-500">
                  좌측에서 회원을 선택하면
                  <br />
                  상세 정보가 표시됩니다
                </div>
              </div>
            </div>
          ) : (
            <MemberDetailPanel
              key={selectedSeq}
              userSeq={selectedSeq}
              onClose={() => setSelectedSeq(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 탭 배지용 건수 조회 — 목록 본문은 버리고 totalElements 만 사용한다.
 */
function useTabCount(
  keyword: string | undefined,
  snsType: MemberSnsTypeCode | undefined,
  testYn?: string,
) {
  const { data } = useQuery({
    queryKey: ['members', 'count', { keyword, snsType, testYn }],
    queryFn: () => membersApi.getMembers({ page: 0, size: 1, keyword, snsType, testYn }),
    select: res => res.totalElements ?? 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  return data;
}
